import { BUILDINGS, ItemType, createEmptyItemCounts } from '../constants';
import { createGameEngine, resetGameEngine } from '../GameEngine';
import { PlayerInventory, createEmptyPlayerInventory } from '../GameState';
import {
  CollectResourceCommand,
  createCollectResourceCommand,
} from '../InputCommands';
import { TestDefinition, assert } from '../TestRunner';

type ChestConfig = {
  buildingKey: keyof typeof BUILDINGS;
  itemType: ItemType;
  storedAmount: number;
};

const CHEST_TEST_CONFIGS: ChestConfig[] = [
  {
    buildingKey: 'IRON_CHEST',
    itemType: 'copper-plate',
    storedAmount: 6,
  },
  {
    buildingKey: 'STEEL_CHEST',
    itemType: 'copper-plate',
    storedAmount: 12,
  },
  {
    buildingKey: 'WAREHOUSE',
    itemType: 'iron-plate',
    storedAmount: 24,
  },
];

const getInventoryCount = (itemType: ItemType, stateSlots: PlayerInventory) =>
  stateSlots.reduce((total, slot) => {
    if (slot.itemType === itemType) {
      return total + slot.count;
    }
    return total;
  }, 0);

const createChestCollectionTest = ({
  buildingKey,
  itemType,
  storedAmount,
}: ChestConfig): TestDefinition => ({
  name: `${BUILDINGS[buildingKey].name} collection adds items to player inventory`,
  description: `Collecting nearby ${BUILDINGS[buildingKey].name.toLowerCase()} transfers its contents to the player inventory via the collect resource command`,
  testFunction: async () => {
    resetGameEngine();

    const chestPosition = { x: 30, y: 30 };
    const playerPosition = { x: 30, y: 29 };
    const chestKey = `${chestPosition.x},${chestPosition.y}`;

    const initialInventory = createEmptyPlayerInventory();
    const initialState = {
      playerPos: playerPosition,
      playerInventory: initialInventory,
      buildingLayer: {
        [chestKey]: BUILDINGS[buildingKey].character,
      },
      chestInventoryLayer: {
        [chestKey]: {
          items: {
            ...createEmptyItemCounts(),
            [itemType]: storedAmount,
          },
        },
      },
    };

    const engine = createGameEngine(initialState);
    const baselineInventoryCount = getInventoryCount(
      itemType,
      engine.getState().playerInventory
    );

    const collectCommand: CollectResourceCommand = createCollectResourceCommand(
      {
        position: chestPosition,
      }
    );

    const handleCollect = (
      engine as unknown as {
        handleCollectResourceCommand: (command: CollectResourceCommand) => void;
      }
    ).handleCollectResourceCommand;
    handleCollect.call(engine, collectCommand);

    const updatedState = engine.getState();
    const updatedInventoryCount = getInventoryCount(
      itemType,
      updatedState.playerInventory
    );

    assert.equals(
      updatedInventoryCount - baselineInventoryCount,
      storedAmount,
      'Collected items should be added to the player inventory'
    );

    const remainingInChest =
      updatedState.chestInventoryLayer[chestKey]?.items[itemType] ?? 0;
    assert.equals(
      remainingInChest,
      0,
      'Chest should be emptied for the collected item type'
    );

    resetGameEngine();

    return {
      name: `${BUILDINGS[buildingKey].name} chest collection`,
      passed: true,
      message: `Collected ${storedAmount} ${itemType} from ${BUILDINGS[buildingKey].name}`,
      duration: 0,
      details: {
        chestType: BUILDINGS[buildingKey].name,
        collectedItem: itemType,
        collectedAmount: storedAmount,
      },
    };
  },
});

export const chestCollectionTests: TestDefinition[] = CHEST_TEST_CONFIGS.map(
  createChestCollectionTest
);
